// F4 · the two number planes, and the absence that is not a number.
//
// Kit 04 items 100, 101, 102, and the rule every other component in Kits 04
// to 06 formats against.
//
//   client     one decimal, weight 700, tracking -0.03em, --plane-client
//   competitor integer,     weight 500, tracking -0.01em, --plane-comp
//
// "The plane encodes WHOSE data a number is, not how important it is." A
// client's own secondary rate stays on the client plane; hierarchy is carried
// by the accent. That is the mistake this file exists to make hard: `plane` is
// a required prop with no default, so nobody picks one by omission.
//
// ── Why formatting lives here and not at each call site ────────────────────
//
// Because a decimal place is a claim about precision. `14.0%` says the
// measurement resolves to a tenth; `14%` says it does not. The kits' second
// commonest defect is a figure retyped at the wrong precision beside the one
// it came from, and the only fix is one function that both call.

import type { ReactNode } from "react";
import { ArrowDown, ArrowUp } from "lucide-react";
import { Icon } from "./icon";
import { cx } from "./kit-state";
import { NOT_MEASURED, NO_MOVEMENT } from "./strings";

/** Whose reading this is. Never "how important". */
export type Plane = "client" | "competitor";

/** How many decimals each plane states. F4, and the only place these live. */
const DECIMALS: Record<Plane, number> = { client: 1, competitor: 0 };

/**
 * The canonical rendering of a rate.
 *
 * `null` and `undefined` are NOT zero and never render as one: the caller gets
 * back the string "Not measured", which is F4's exact wording and the same
 * constant `Readout` and `Unmeasured` use.
 */
export function formatRate(value: number | null | undefined, plane: Plane, unit = "%"): string {
  if (value === null || value === undefined || Number.isNaN(value)) return NOT_MEASURED;
  return `${value.toFixed(DECIMALS[plane])}${unit}`;
}

/** A count, an index, a number of models. No unit, no forced decimals. */
export function formatCount(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return NOT_MEASURED;
  return String(value);
}

export interface FigureProps {
  /** The reading. null renders "Not measured" — never 0, never a dash. */
  value: number | null | undefined;
  /** Required. There is no sensible default and a default would be picked by
      accident on the first screen that forgot. */
  plane: Plane;
  /** "%" by default. Pass "" for an index or a count. */
  unit?: string;
  /** "sm" is a table cell, "md" a card, "lg" a stage, "xl" a hero. */
  size?: "sm" | "md" | "lg" | "xl";
  className?: string;
}

/**
 * One figure, on one plane.
 *
 * The size is separate from the plane on purpose. A competitor figure in a
 * card and a competitor figure in a table cell are the same plane at two
 * sizes; folding size into the plane is how "make it smaller" becomes "move it
 * to the competitor plane" and the reader loses track of whose number it is.
 */
export function Figure({ value, plane, unit = "%", size = "md", className }: FigureProps) {
  const missing = value === null || value === undefined || Number.isNaN(value);
  return (
    <span
      className={cx("k-fig", `k-fig--${size}`, missing && "k-fig--absent", className)}
      data-plane={plane}
      data-num="true"
    >
      {formatRate(value, plane, unit)}
    </span>
  );
}

/* ═══ 102 · attribute score rows ════════════════════════════════════════════
   redline: client 32/700/-0.03em · competitor 20/500/-0.01em · row gap 12 · bar 6

   The bar's length is DERIVED from the score against the scale's maximum. The
   specimen draws 6.8 and 4.1 out of ten; passing a width beside a score is how
   a bar comes to disagree with the number printed next to it.

   An unmeasured attribute draws NO bar. Not a zero-length one: a zero-length
   bar at the left edge of a track is a measured zero, which is a different and
   false claim.
   ═════════════════════════════════════════════════════════════════════════ */

export interface ScoreRowProps {
  label: string;
  /** null is unmeasured. */
  score: number | null;
  /** The top of the scale the score is on. The specimen's is ten. */
  outOf: number;
  plane?: Plane;
  className?: string;
}

export function ScoreRow({ label, score, outOf, plane = "client", className }: ScoreRowProps) {
  const measured = score !== null && !Number.isNaN(score);
  const share = measured ? Math.max(0, Math.min(1, score / outOf)) : 0;
  return (
    <div className={cx("k-score", className)}>
      <div className="k-score__left">
        <span className="k-score__label">{label}</span>
        <span className="k-score__track">
          {/* No bar at all when there is nothing to draw. A zero-width fill is
              still a fill, and it reads as a measured zero. */}
          {measured ? <span className="k-score__bar" style={{ width: `${share * 100}%` }} /> : null}
        </span>
      </div>
      {measured
        ? <Figure value={score} plane={plane} unit="" size="sm" />
        : <span className="k-absent">{NOT_MEASURED}</span>}
    </div>
  );
}

/* ═══ 91 · delta ════════════════════════════════════════════════════════════
   redline: icon 14 · gap 8 · null renders nothing
   keys: Read with the value it qualifies, never announced separately.

   Four cases and they are not symmetric:
     up    --delta-pos, an arrow, "+N pts"
     down  --delta-neg, an arrow, the minus sign U+2212, "−N pts"
     flat  --delta-flat, NO arrow, the words "No movement"
     null  NOTHING. Not 0.0, not a dash, not an empty span.

   "A rising rate is the brand accent, a falling one is the gap accent. Never
   the error colour: a declining rate is a gap, not a fault."
   ═════════════════════════════════════════════════════════════════════════ */

export interface DeltaProps {
  /** In points. null renders nothing at all. */
  value: number | null | undefined;
  /** "over 8 weeks". Appended to the figure, never instead of it. */
  window?: string;
  /** One decimal, like the client plane it qualifies. */
  decimals?: number;
  className?: string;
}

export function Delta({ value, window, decimals = 1, className }: DeltaProps) {
  // The whole rule, and it is the first line for a reason: an absent delta is
  // absent. Returning null here is what makes design-lint Rule 11 true of this
  // component rather than aspirational.
  if (value === null || value === undefined || Number.isNaN(value)) return null;

  if (value === 0) {
    return <span className={cx("k-delta", className)} data-dir="flat">{NO_MOVEMENT}</span>;
  }

  const up = value > 0;
  const magnitude = Math.abs(value).toFixed(decimals);
  // U+2212 MINUS SIGN, not a hyphen. A hyphen at tabular width is a third the
  // height of the plus it has to balance against in the column above it.
  const signed = up ? `+${magnitude}` : `−${magnitude}`;

  return (
    <span className={cx("k-delta", className)} data-dir={up ? "up" : "down"}>
      <Icon icon={up ? ArrowUp : ArrowDown} size="sm" />
      <span data-num="true">{signed} pts{window ? ` ${window}` : ""}</span>
    </span>
  );
}

/** An absence, wherever one has to be stated in place of a figure. */
export function Absent({ children = NOT_MEASURED, className }: { children?: ReactNode; className?: string }) {
  return <span className={cx("k-absent", className)}>{children}</span>;
}

export { NOT_MEASURED, NO_MOVEMENT };
